import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import { FloorType } from "building-types/floors/floorTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { GeneratedStairSystem } from "building-types/stairs/stairTypes";
import { StairPlanes } from "building-types/stairs/stairTypesEnum";
import { WORLD_ONE_INCH, WORLD_ZERO_INCHES } from "common/constants/world";
import type { RWTable, Table } from "common/data/table";
import type { LevelInches, WorldInches } from "common/types/nominalNumbersTypes";
import {
  levelInchesNew,
  levelInchesToWorld,
  numAdd,
  worldInchesNew,
  worldInchesTolevel,
} from "common/utils/nominalNumbers";
import { floorAssemblyBaseOffset } from "doc/elements/floor/floorAssembly";
import { aabb2ByPoints } from "geom/aabb2";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { intersectPolyLine2PolyLine2 } from "geom/intersect";
import { CurveCurvePointIntersection } from "geom/intersectLine2Line2";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2EdgeEval,
  polyLine2EdgeTangent,
  polyLine2Length,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ByArray, polyLine3Length } from "geom/polyLine3";
import type { IndexAABB2 } from "geom/rtree";
import { rtreeAddPolyLine2, rtreePolyLine2s } from "geom/rtree";
import { vec2Dot, vec2Len } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import RBush from "rbush";

interface LevelSurfaces {
  readonly levelRoomSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelRoomSurfaces: Table<Surface[]>;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
}

export interface Path3OnSurfaces {
  readonly polyLine3: PolyLine3 | undefined;
  readonly gapPolyLine3s: PolyLine3[]; // railing paths are broken into separate paths based on collinearity, gaps exist when direction changes and heights are resolved differently on each edge
  readonly vertexAdditionalHeights: number[];
  readonly pathSurfaceOverlaps: PathSurfaceOverlap[];
  readonly associatedWithStairs: boolean;
  readonly terminationLogicWouldChange: boolean;
}

export interface SurfaceSlope {
  readonly dirX: number; // unit direction of ascent in plan (2D)
  readonly dirY: number;
  readonly rise: number; // height per unit distance along dir
}

export interface Surface {
  readonly outline: PolyLine2;
  readonly height: LevelInches;
  readonly slope?: SurfaceSlope;
}

// mutable fields
interface PathSurfaceOverlap {
  readonly paramStart: number;
  readonly paramEnd: number;
  height: number; // in resolved overlaps this is the actual height, in unresolved this is the undefined (and solved later by assuming height of nearest resolved overlap)
  readonly rate: number; // slope of the overall stair surface, projected onto the edge direction
}

interface UnresolvedPathSurfaceOverlap {
  readonly paramStart: number;
  readonly paramEnd: number;
  height: number | undefined; // in resolved overlaps this is the actual height, in unresolved this is the undefined (and solved later by assuming height of nearest resolved overlap)
  readonly rate: number;
}

interface PathPoint {
  readonly param: number;
  readonly height: number;
  gapHeight?: number;
}

interface PathSurfaceOverlapCollection {
  readonly resolved: PathSurfaceOverlap[];
  readonly unresolved: PathSurfaceOverlap[];
  readonly totalLength: number;
  readonly overlapDetected?: boolean;
}

const IGNORE_GAP_THRESHOLD = 0.1;
const IGNORE_HEIGHT_CHANGE_THRESHOLD = 0.1;

export function makeLevelFloorAndStairSurfaces(
  levels: Table<Level>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  roomFloorAssemblies: Table<FloorAssembly>,
  stairSystems: Table<GeneratedStairSystem>,
  floorSystems: Table<FloorSystem>,
): LevelSurfaces {
  const levelRoomSurfaceRTree: RWTable<RBush<IndexAABB2>> = {};
  const levelRoomSurfaces: RWTable<Surface[]> = {};
  const levelStairSurfaceRTree: RWTable<RBush<IndexAABB2>> = {};
  const levelStairSurfaces: RWTable<Surface[]> = {};
  const levelPerimeterSurfaceRTree: RWTable<RBush<IndexAABB2>> = {};
  const levelPerimeterSurfaces: RWTable<Surface[]> = {};

  for (const levelId in levels) {
    const { roomClassFloors = [] } = floorSystems[levelId] ?? {};

    levelPerimeterSurfaces[levelId] = [];
    levelPerimeterSurfaceRTree[levelId] = new RBush<IndexAABB2>();
    let perimIdx = 0;

    const level = levels[levelId];

    for (let f = 0; f < roomClassFloors.length; f++) {
      const floors = roomClassFloors[f];

      const surfaces: Surface[] = [];
      const perimeters: Float64Array[] = [];

      for (let i = 0; i < floors.length; i++) {
        const floor = floors[i];
        if (floor.floorType !== FloorType.SOLID) {
          continue;
        }
        for (let j = 0; j < floor.floorPlate.perimeterPolyLine2s.length; j++) {
          const perimeter = floor.floorPlate.perimeterPolyLine2s[j];
          perimeters.push(perimeter);
          surfaces.push({
            outline: perimeter,
            height: worldInchesTolevel(worldInchesNew(floor.floorElevation), level.floorElevation),
          });
          rtreeAddPolyLine2(levelPerimeterSurfaceRTree[levelId], perimeter, perimIdx++);
        }
      }

      levelPerimeterSurfaces[levelId].push(...surfaces);
    }

    const roomIds = roomsByLevel[levelId] ?? [];
    const roomSurfaces: Surface[] = [];
    for (const roomId of roomIds) {
      const room = rooms[roomId];
      const floor = roomFloorAssemblies[roomId];
      const height = floor ? floorAssemblyBaseOffset(floor) : levelInchesNew(0);
      if (room) {
        roomSurfaces.push({ outline: room.outline, height });
      }
    }
    levelRoomSurfaces[levelId] = roomSurfaces;
    levelRoomSurfaceRTree[levelId] = rtreePolyLine2s(roomSurfaces.map((x) => x.outline));
  }

  for (const i in levels) {
    const { id: levelId } = levels[i];
    const stairSurfaces: Surface[] = [];
    for (const stairSystem of Object.values(stairSystems).filter((x) => x.levelId === levelId)) {
      for (const stairBlockId in stairSystem.blocks) {
        const block = stairSystem.blocks[stairBlockId];

        let slope: SurfaceSlope | undefined;
        const backPlane = block.planes[StairPlanes.BACK];
        if (backPlane) {
          const { vX, vY, vZ } = backPlane;
          const horizontalLen = vec2Len(vX, vY);
          if (horizontalLen > DIST_EPSILON) {
            slope = {
              dirX: vX / horizontalLen,
              dirY: vY / horizontalLen,
              rise: vZ / horizontalLen,
            };
          }
        }

        for (const index of Object.keys(block.stairPlanPolys)) {
          const stairPlanPoly = block.stairPlanPolys[index];
          if (!stairPlanPoly) {
            continue;
          }
          stairSurfaces.push({
            outline: stairPlanPoly.outline,
            height: levelInchesNew(stairPlanPoly.height),
            slope,
          });
        }
      }
    }

    levelStairSurfaces[levelId] = stairSurfaces;
    levelStairSurfaceRTree[levelId] = rtreePolyLine2s(stairSurfaces.map((x) => x.outline));
  }

  return {
    levelRoomSurfaceRTree,
    levelRoomSurfaces,
    levelStairSurfaceRTree,
    levelStairSurfaces,
    levelPerimeterSurfaceRTree,
    levelPerimeterSurfaces,
  };
}

function addOverlapSurfaces(
  surfaceRTree: RBush<IndexAABB2>,
  surfaces: Surface[],
  edge2: PolyLine2,
  overlaps: PathSurfaceOverlapCollection[],
): boolean {
  const { resolved, unresolved, totalLength, overlapDetected } = getPathSurfaceOverlap(surfaceRTree, surfaces, edge2);
  overlaps.push({
    resolved: resolved.sort((a, b) => a.paramStart - b.paramStart),
    unresolved: unresolved.sort((a, b) => a.paramStart - b.paramStart),
    totalLength,
  });
  return !!overlapDetected;
}

interface MakePolyLine3OnFloorAndStairSurfacesOptions {
  readonly path2: PolyLine2[];
  readonly levelId: string;
  readonly levels: Table<Level>;
  readonly levelRoomSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelRoomSurfaces: Table<Surface[]>;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
  readonly useNewRailingTerminationLogic: boolean;
}

export function makePolyLine3OnFloorAndStairSurfaces({
  path2,
  levelId,
  levels,
  levelRoomSurfaceRTree,
  levelRoomSurfaces,
  levelStairSurfaceRTree,
  levelStairSurfaces,
  levelPerimeterSurfaceRTree,
  levelPerimeterSurfaces,
  useNewRailingTerminationLogic,
}: MakePolyLine3OnFloorAndStairSurfacesOptions): Path3OnSurfaces[] {
  let stairOverlapDetected = false;
  const path3s: Path3OnSurfaces[] = [];
  const pathSurfaceOverlapCollections: PathSurfaceOverlapCollection[] = [];
  // path surface overlap preflight
  for (let i = 0; i < path2.length; i++) {
    const edge2 = path2[i];
    const stairSurfaces = levelStairSurfaces[levelId];
    const stairSurfaceRTree = levelStairSurfaceRTree[levelId];
    if (!edge2) {
      pathSurfaceOverlapCollections.push({
        resolved: [],
        unresolved: [],
        totalLength: 0,
      });
    }
    if (addOverlapSurfaces(stairSurfaceRTree, stairSurfaces, edge2, pathSurfaceOverlapCollections)) {
      stairOverlapDetected = true;
    }
  }

  const edgeTerminalRates: { firstRate: number; lastRate: number }[] = [];
  for (let i = 0; i < path2.length; i++) {
    const { resolved, unresolved } = pathSurfaceOverlapCollections[i];
    let firstRate = 0;
    let lastRate = 0;
    if (resolved.length > 0) {
      const firstResolved = resolved[0];
      firstRate = firstResolved.rate;
      const lastResolved = resolved[resolved.length - 1];
      lastRate = lastResolved.rate;

      // the first and/or last segment may be unresolved meaning it's on a
      // landing or floor, so the rate should be 0
      if (unresolved.length > 0) {
        const firstUnresolved = unresolved[0];
        const lastUnresolved = unresolved[unresolved.length - 1];
        if (firstUnresolved.paramStart < firstResolved.paramStart) {
          firstRate = 0;
        }
        if (lastUnresolved.paramEnd > lastResolved.paramEnd) {
          lastRate = 0;
        }
      }
    }
    edgeTerminalRates.push({ firstRate, lastRate });
  }

  let lastPoint: Vec3 | undefined;
  for (let i = 0; i < path2.length; i++) {
    const edge = path2[i];

    let { overlaps: pathSurfaceOverlaps } = getEdgePathSurfaceOverlaps(
      pathSurfaceOverlapCollections,
      edge,
      i,
      levelId,
      levels,
      levelRoomSurfaces,
      levelRoomSurfaceRTree,
    );

    if (pathSurfaceOverlaps.length === 0) {
      ({ overlaps: pathSurfaceOverlaps } = getEdgePathSurfaceOverlaps(
        pathSurfaceOverlapCollections,
        edge,
        i,
        levelId,
        levels,
        levelPerimeterSurfaces,
        levelPerimeterSurfaceRTree,
      ));
    }

    const { points: pathPoints3, terminationLogicWouldChange } = convertOverlapsToPathPoints(
      pathSurfaceOverlaps,
      i > 0 ? edgeTerminalRates[i - 1].lastRate : undefined,
      i < path2.length - 1 ? edgeTerminalRates[i + 1].firstRate : undefined,
      useNewRailingTerminationLogic,
    );

    let path3: Path3OnSurfaces;
    ({ path3, lastPoint } = convertPathPointsToPath3OnSurfaces(
      pathSurfaceOverlaps,
      pathPoints3,
      edge,
      lastPoint,
      stairOverlapDetected,
      terminationLogicWouldChange,
    ));

    path3s.push(path3);
  }

  return path3s;
}

function convertPathPointsToPath3OnSurfaces(
  pathSurfaceOverlaps: PathSurfaceOverlap[],
  pathPoints3: PathPoint[],
  edge: PolyLine2,
  lastPoint: Vec3 | undefined,
  stairOverlapDetected: boolean,
  terminationLogicWouldChange: boolean,
) {
  const pts3: number[] = [];
  const railGapPoints3: number[][] = [];
  const pointsLength = pathPoints3.length;
  const vertexAdditionalHeights: number[] = [];

  for (let i = 0; i < pointsLength; i++) {
    const point = pathPoints3[i];
    const [x, y] = polyLine2EdgeEval(edge, 0, point.param);
    pts3.push(x, y, point.height);
    vertexAdditionalHeights.push(point.gapHeight ?? 0);

    if (i === 0 && lastPoint !== undefined) {
      railGapPoints3.push([lastPoint[0], lastPoint[1], lastPoint[2]], [x, y, point.height]);
    }

    if (i === pointsLength - 1) {
      lastPoint = [x, y, point.height];
    }
  }

  const polyLine3 = polyLine3ByArray(pts3);
  const gapPolyLine3s: PolyLine3[] = [];
  if (railGapPoints3.length > 0) {
    for (const gapPoints of railGapPoints3) {
      const gapPolyLine3 = polyLine3ByArray(gapPoints);
      gapPolyLine3s.push(gapPolyLine3);
    }
  }

  const path3: Path3OnSurfaces = {
    polyLine3: polyLine3Length(polyLine3) > DIST_EPSILON ? polyLine3 : undefined,
    gapPolyLine3s,
    vertexAdditionalHeights,
    pathSurfaceOverlaps,
    associatedWithStairs: stairOverlapDetected,
    terminationLogicWouldChange,
  };

  return { path3, lastPoint };
}

function convertOverlapsToPathPoints(
  pathSurfaceOverlaps: PathSurfaceOverlap[],
  prevEdgeLastRate: number | undefined,
  nextEdgeFirstRate: number | undefined,
  useNewRailingTerminationLogic: boolean,
) {
  const points: PathPoint[] = [];
  let terminationLogicWouldChange = false;
  const pathOverlapCount = pathSurfaceOverlaps.length;

  /*
  Note to future railings dev [SZ]: I added awareness of the overall slope of the stair run to
  overlaps to resolve a small bug. This awareness opens the door to a rework of the logic below
  to be much simpler (did the slope change? If yes, add a point. If not, don't). However, that
  rework exposed some underlying geometry issues in overlap detection, particularly with 
  railings next to stairs that have been cut by walls. In the interest of not turning a bug fix
  into a multi-day geometric-edge-case-busting mission, I've left the existing logic intact and
  only added a few small edge cases. Maybe you, future dev, will have time to tackle the
  underlying issues and clean up this logic. Findings documented in BDG-2839 
  (https://linear.app/higharc/issue/BDG-2839/robustness-in-railing-overlap-detection-and-path-creation)
  */

  for (let i = 0; i < pathOverlapCount; i++) {
    const isTerminalOverlap = i === 0 || i === pathOverlapCount - 1;
    const currentOverlap = pathSurfaceOverlaps[i];

    //these are for height increasing
    const nextOverlap = pathSurfaceOverlaps[i + 1];
    const nextOverlapRise = i + 1 < pathOverlapCount && nextOverlap ? nextOverlap.height - currentOverlap.height : 0;
    const nextOverlapHeight = i + 1 < pathOverlapCount ? nextOverlap.height : currentOverlap.height;
    const nextOverlapDist = i + 1 < pathOverlapCount ? nextOverlap.paramEnd - nextOverlap.paramStart : 0;

    const secondNextOverlapRise = i + 2 < pathOverlapCount ? pathSurfaceOverlaps[i + 2].height - nextOverlapHeight : 0;
    const nextSlope = nextOverlapDist !== 0 ? secondNextOverlapRise / nextOverlapDist : 0;

    //these are for height decreasing
    const previousOverlap = pathSurfaceOverlaps[i - 1];
    const previousOverlapHeight = i - 1 >= 0 ? previousOverlap.height : currentOverlap.height;
    const previousOverlapDist = i - 1 > 0 ? previousOverlap.paramEnd - previousOverlap.paramStart : 0;
    const secondPreviousOverlapHeight = i - 2 > 0 ? pathSurfaceOverlaps[i - 2].height : previousOverlapHeight;
    const previousSlope =
      previousOverlapDist !== 0 ? (secondPreviousOverlapHeight - previousOverlapHeight) / previousOverlapDist : 0;

    const nextIncreasing = currentOverlap.height < nextOverlapHeight - DIST_EPSILON;
    const prevDecreasing = currentOverlap.height < previousOverlapHeight - DIST_EPSILON;

    const currentSlopeGoingUp = nextOverlapRise / (currentOverlap.paramEnd - currentOverlap.paramStart);
    const currentSlopeGoingDown =
      (previousOverlapHeight - currentOverlap.height) / (currentOverlap.paramEnd - currentOverlap.paramStart);

    if (nextIncreasing && prevDecreasing) {
      if (nextSlope > currentSlopeGoingUp + DIST_EPSILON && previousSlope > currentSlopeGoingDown + DIST_EPSILON) {
        const newParamDist2 = previousSlope !== 0 ? (previousOverlapHeight - currentOverlap.height) / previousSlope : 0;
        points.push({ param: currentOverlap.paramStart + newParamDist2, height: currentOverlap.height });
        const newParamDist = (nextOverlapHeight - currentOverlap.height) / nextSlope;
        points.push({ param: currentOverlap.paramEnd - newParamDist, height: currentOverlap.height });
      }
    } else if (nextIncreasing) {
      if (nextSlope > currentSlopeGoingUp + DIST_EPSILON) {
        //slope increasing
        points.push({ param: currentOverlap.paramStart, height: currentOverlap.height });
        const newParamDist = (nextOverlapHeight - currentOverlap.height) / nextSlope;
        points.push({ param: currentOverlap.paramEnd - newParamDist, height: currentOverlap.height });
      } else if (currentSlopeGoingUp > nextSlope + DIST_EPSILON && nextSlope > DIST_EPSILON) {
        //slope decreasing
        points.push({
          param: currentOverlap.paramStart,
          height: isTerminalOverlap
            ? nextOverlapHeight - (currentOverlap.paramEnd - currentOverlap.paramStart) * nextSlope
            : currentOverlap.height,
          gapHeight: isTerminalOverlap
            ? nextOverlapHeight -
              (currentOverlap.paramEnd - currentOverlap.paramStart) * nextSlope -
              currentOverlap.height
            : undefined,
        });
      } else {
        //slope the same
        //this is the path intersection with the toe side of the tread, is useful for adding posts on treads (not using now, but likely will later)
        //points.push({ param: currentOverlap.paramStart, height: currentOverlap.height });
      }
    } else if (prevDecreasing) {
      if (previousSlope > currentSlopeGoingDown + DIST_EPSILON) {
        //slope decreasing
        const newParamDist2 = previousSlope !== 0 ? (previousOverlapHeight - currentOverlap.height) / previousSlope : 0;
        points.push({ param: currentOverlap.paramStart + newParamDist2, height: currentOverlap.height });
        points.push({ param: currentOverlap.paramEnd, height: currentOverlap.height });
      } else if (currentSlopeGoingDown > previousSlope + DIST_EPSILON && previousSlope > DIST_EPSILON) {
        //slope increasing
        points.push({
          param: currentOverlap.paramEnd,
          height: isTerminalOverlap
            ? previousOverlapHeight - (currentOverlap.paramEnd - currentOverlap.paramStart) * previousSlope
            : currentOverlap.height,
          gapHeight: isTerminalOverlap
            ? previousOverlapHeight -
              (currentOverlap.paramEnd - currentOverlap.paramStart) * previousSlope -
              currentOverlap.height
            : undefined,
        });
      } else {
        //slope the same
        //this is the path intersection with the toe side of the tread, is useful for adding posts on treads (not using now, but likely will later)
        //points.push({ param: currentOverlap.paramStart, height: currentOverlap.height });
      }
    } else {
      // check for edge cases at the ends
      const nextDecreasing = nextOverlapHeight < currentOverlap.height - DIST_EPSILON;
      const prevIncreasing = previousOverlapHeight < currentOverlap.height - DIST_EPSILON;
      const prevEdgeFlat = prevEdgeLastRate !== undefined && Math.abs(prevEdgeLastRate) < DIST_EPSILON;
      const nextEdgeFlat = nextEdgeFirstRate !== undefined && Math.abs(nextEdgeFirstRate) < DIST_EPSILON;
      const currentFlat = Math.abs(currentOverlap.rate) < DIST_EPSILON;

      const startTerminatesOnSlope = i === 0 && nextDecreasing && !prevEdgeFlat && !currentFlat;
      const endTerminatesOnSlope = i === pathOverlapCount - 1 && prevIncreasing && !nextEdgeFlat && !currentFlat;

      if ((startTerminatesOnSlope || endTerminatesOnSlope) && !useNewRailingTerminationLogic) {
        terminationLogicWouldChange = true;
      }

      if (useNewRailingTerminationLogic && startTerminatesOnSlope) {
        const height =
          currentOverlap.height - (currentOverlap.paramEnd - currentOverlap.paramStart) * currentOverlap.rate;
        points.push({
          param: currentOverlap.paramStart,
          height,
          gapHeight: height - currentOverlap.height,
        });
      } else if (useNewRailingTerminationLogic && endTerminatesOnSlope) {
        const height =
          currentOverlap.height + (currentOverlap.paramEnd - currentOverlap.paramStart) * currentOverlap.rate;
        points.push({
          param: currentOverlap.paramEnd,
          height,
          gapHeight: height - currentOverlap.height,
        });
      } else {
        //flat
        points.push({ param: currentOverlap.paramStart, height: currentOverlap.height });
        points.push({ param: currentOverlap.paramEnd, height: currentOverlap.height });
      }
    }
  }
  return { points, terminationLogicWouldChange };
}

function getPathSurfaceOverlap(
  surfaceRTree: RBush<IndexAABB2> | undefined,
  surfaces: Surface[],
  pathEdge: PolyLine2,
  additionalParamStartFromPreviousEdgeLengths = 0,
  additionalHeightForNextLevel = 0,
): PathSurfaceOverlapCollection {
  const resolvedOverlaps: PathSurfaceOverlap[] = [];
  const unresolvedOverlaps: UnresolvedPathSurfaceOverlap[] = [];
  let overlapDetected = false;

  const searchBox = polyLine2AABB2(pathEdge);
  const pathSegmentLength = polyLine2Length(pathEdge);
  const potentialSurfaceIndices = surfaceRTree ? surfaceRTree.search(searchBox) : [];

  for (const surfaceItem of potentialSurfaceIndices) {
    const { outline, height, slope } = surfaces[surfaceItem.index];
    const ints = intersectPolyLine2PolyLine2(pathEdge, outline);
    if (!ints || ints.length === 0) {
      continue;
    }
    overlapDetected = true;

    const overlapParams: number[] = [];
    for (const int of ints) {
      const { int: curveInt } = int;
      if (curveInt && curveInt instanceof CurveCurvePointIntersection) {
        const { param0 } = curveInt as CurveCurvePointIntersection;
        overlapParams.push(param0);
      }
    }

    if (overlapParams.length > 1) {
      overlapParams.sort((a, b) => a - b);
      // get the rate WRT this path edge
      let rate = 0;
      if (slope !== undefined) {
        const [edgeDirX, edgeDirY] = polyLine2EdgeTangent(pathEdge, 0);
        rate = vec2Dot(edgeDirX, edgeDirY, slope.dirX, slope.dirY) * slope.rise;
      }

      // loop through only capturing pairs (known edge cases not handled)
      for (let i = 0; i < overlapParams.length - 1; i += 2) {
        resolvedOverlaps.push({
          paramStart: overlapParams[i],
          paramEnd: overlapParams[i + 1],
          height: height + additionalHeightForNextLevel,
          rate,
        });
      }
    }
  }

  resolvedOverlaps.sort((a, b) => a.paramStart - b.paramStart);

  // This next section tests the ends of the path. In case, the start or the end lands in the middle of a surface.
  const overlapCount = resolvedOverlaps.length;
  if (overlapCount === 0) {
    const [x, y] = polyLine2EdgeEval(pathEdge, 0, 0.5, true);
    const pathSegmentStartSurfaceIndices = surfaceRTree ? surfaceRTree.search(aabb2ByPoints([[x, y]])) : [];

    let maxHeight: number | undefined = undefined;
    let slopeAtMaxHeight: SurfaceSlope | undefined = undefined;
    for (const surfaceItem of pathSegmentStartSurfaceIndices) {
      const { outline, height, slope } = surfaces[surfaceItem.index];

      if (classifyContainmentVec2PolyLine2Fast(x, y, outline)) {
        if (maxHeight === undefined || (maxHeight !== undefined && height > maxHeight)) {
          maxHeight = height;
          slopeAtMaxHeight = slope;
        }
      }
    }

    if (maxHeight !== undefined) {
      let rate = 0;
      if (slopeAtMaxHeight !== undefined) {
        const [edgeDirX, edgeDirY] = polyLine2EdgeTangent(pathEdge, 0);
        rate = vec2Dot(edgeDirX, edgeDirY, slopeAtMaxHeight.dirX, slopeAtMaxHeight.dirY) * slopeAtMaxHeight.rise;
      }
      resolvedOverlaps.push({
        paramStart: additionalParamStartFromPreviousEdgeLengths,
        paramEnd: pathSegmentLength + additionalParamStartFromPreviousEdgeLengths,
        height: maxHeight + additionalHeightForNextLevel,
        rate,
      });
    } else {
      unresolvedOverlaps.push({
        paramStart: additionalParamStartFromPreviousEdgeLengths,
        paramEnd: pathSegmentLength + additionalParamStartFromPreviousEdgeLengths,
        height: undefined,
        rate: 0,
      });
    }
  } else {
    let lastOverlapParamEnd = 0;
    for (const overlapIndex of Object.keys(resolvedOverlaps)) {
      const { paramStart, paramEnd } = resolvedOverlaps[overlapIndex];

      //if first overlap is not a param 0 of the path, search rooms for pertinent overlaps, using next overlap as a height guide
      if (paramStart - lastOverlapParamEnd > 1) {
        const [x, y] = polyLine2EdgeEval(pathEdge, 0, (paramStart - lastOverlapParamEnd) / 2 + lastOverlapParamEnd);
        const pathSegmentStartStairSurfaceIndices = surfaceRTree ? surfaceRTree.search(aabb2ByPoints([[x, y]])) : [];

        let maxHeight: number | undefined = undefined;
        let slopeAtMaxHeight: SurfaceSlope | undefined = undefined;
        for (const stairItem of pathSegmentStartStairSurfaceIndices) {
          const { outline, height, slope } = surfaces[stairItem.index];

          if (classifyContainmentVec2PolyLine2Fast(x, y, outline)) {
            if (maxHeight === undefined || (maxHeight !== undefined && height > maxHeight)) {
              maxHeight = height;
              slopeAtMaxHeight = slope;
            }
          }
        }

        if (maxHeight !== undefined) {
          let rate = 0;
          if (slopeAtMaxHeight !== undefined) {
            const [edgeDirX, edgeDirY] = polyLine2EdgeTangent(pathEdge, 0);
            rate = vec2Dot(edgeDirX, edgeDirY, slopeAtMaxHeight.dirX, slopeAtMaxHeight.dirY) * slopeAtMaxHeight.rise;
          }
          resolvedOverlaps.push({
            paramStart: lastOverlapParamEnd,
            paramEnd: paramStart,
            height: maxHeight + additionalHeightForNextLevel,
            rate,
          });
        } else {
          unresolvedOverlaps.push({
            paramStart: lastOverlapParamEnd,
            paramEnd: paramStart,
            height: undefined,
            rate: 0,
          });
        }
      }

      // if gap at end
      if (+overlapIndex === overlapCount - 1 && Math.abs(paramEnd - pathSegmentLength) > DIST_EPSILON) {
        const [x, y] = polyLine2EdgeEval(pathEdge, 0, pathSegmentLength);
        const pathSegmentStartStairSurfaceIndices = surfaceRTree ? surfaceRTree.search(aabb2ByPoints([[x, y]])) : [];

        let maxHeight: number | undefined = undefined;
        let slopeAtMaxHeight: SurfaceSlope | undefined = undefined;
        for (const stairItem of pathSegmentStartStairSurfaceIndices) {
          const { outline, height, slope } = surfaces[stairItem.index];

          if (classifyContainmentVec2PolyLine2Fast(x, y, outline)) {
            if (maxHeight === undefined || (maxHeight !== undefined && height > maxHeight)) {
              maxHeight = height;
              slopeAtMaxHeight = slope;
            }
          }
        }

        if (maxHeight !== undefined) {
          let rate = 0;
          if (slopeAtMaxHeight !== undefined) {
            const [edgeDirX, edgeDirY] = polyLine2EdgeTangent(pathEdge, 0);
            rate = vec2Dot(edgeDirX, edgeDirY, slopeAtMaxHeight.dirX, slopeAtMaxHeight.dirY) * slopeAtMaxHeight.rise;
          }
          resolvedOverlaps.unshift({
            paramStart: paramEnd,
            paramEnd: pathSegmentLength,
            height: maxHeight + additionalHeightForNextLevel,
            rate,
          });
        } else {
          unresolvedOverlaps.push({
            paramStart: paramEnd,
            paramEnd: pathSegmentLength,
            height: undefined,
            rate: 0,
          });
        }
      }

      lastOverlapParamEnd = paramEnd;
    }
  }

  // for any unresolved, find the nearest resolved height on the path and grab the height
  for (const overlap of unresolvedOverlaps) {
    const { paramStart, paramEnd } = overlap;

    let minDistToOverlap = Number.MAX_VALUE;
    let nearestHeight: number | undefined = undefined;

    for (const overlap of resolvedOverlaps) {
      const { paramStart: resolvedParamStart, paramEnd: resolvedParamEnd, height } = overlap;

      const dist = Math.min(Math.abs(paramStart - resolvedParamEnd), Math.abs(paramEnd - resolvedParamStart));
      if (dist < minDistToOverlap) {
        nearestHeight = height;
        minDistToOverlap = dist;
      }
    }

    overlap.height = nearestHeight;
  }

  return {
    resolved: resolvedOverlaps,
    unresolved: unresolvedOverlaps as PathSurfaceOverlap[], // this is safe because we just set the height
    totalLength: pathSegmentLength,
    overlapDetected,
  };
}

function getEdgePathSurfaceOverlaps(
  collections: PathSurfaceOverlapCollection[],
  pathSegment: PolyLine2,
  edgeIndex: number,
  levelId: string,
  levels: Table<Level>,
  levelRoomSurfaces: Table<Surface[]>, // levelId => Surface[]
  levelRoomSurfaceRTree: Table<RBush<IndexAABB2>>, // levelId => RBush
): { overlaps: PathSurfaceOverlap[] } {
  const allOverlaps: PathSurfaceOverlap[] = [];

  const level = levels[levelId];

  const roomSurfaces = levelRoomSurfaces[levelId];
  const roomSurfaceRTree = levelRoomSurfaceRTree[levelId];
  const roomsAboveSurfaces = level.levelAbove ? levelRoomSurfaces[level.levelAbove] ?? [] : [];
  const roomsAboveSurfaceRTree = level.levelAbove ? levelRoomSurfaceRTree[level.levelAbove] : undefined;

  const { resolved, unresolved } = collections[edgeIndex];
  allOverlaps.push(...resolved);

  for (const unresolvedOverlap of unresolved) {
    const { paramStart, paramEnd } = unresolvedOverlap;
    let nearestHeightInResolvedOverlap: number | undefined = unresolvedOverlap.height;

    if (resolved.length === 0) {
      let foundNeighboringOverlap = false;
      let bestHeight: number | undefined = undefined;
      let distForward = 0;
      const length = collections.length;
      if (edgeIndex + 1 < length) {
        for (let i = edgeIndex + 1; i < length; i++) {
          const { resolved, totalLength } = collections[i];
          distForward += totalLength;
          if (resolved.length === 0) {
            continue;
          }

          const firstOverlap = resolved[0];
          bestHeight = firstOverlap.height;
          foundNeighboringOverlap = true;
          break;
        }
      }

      let distBackward = 0;
      if (edgeIndex > 0) {
        for (let i = edgeIndex - 1; i >= 0; i--) {
          const { resolved, totalLength } = collections[i];

          distBackward += totalLength;
          if (resolved.length === 0) {
            continue;
          }

          const lastOverlap = resolved[resolved.length - 1];
          if (foundNeighboringOverlap && distBackward > distForward) {
            break;
          }

          foundNeighboringOverlap = true;
          bestHeight = lastOverlap.height;
          break;
        }
      }
      if (foundNeighboringOverlap) {
        nearestHeightInResolvedOverlap = bestHeight;
      }
    }

    if (nearestHeightInResolvedOverlap === undefined) {
      nearestHeightInResolvedOverlap = levels[levelId].floorElevation;
    }

    const [sx, sy] = polyLine2EdgeEval(pathSegment, 0, paramStart);
    const [ex, ey] = polyLine2EdgeEval(pathSegment, 0, paramEnd);
    const unresolvedOverlapPath = polyLine2ByTwoPoints(sx, sy, ex, ey);

    const { resolved: levelOverlaps } =
      roomSurfaces.length > 0
        ? getPathSurfaceOverlap(
            roomSurfaceRTree,
            roomSurfaces,
            unresolvedOverlapPath,
            paramStart,
            levels[levelId].floorElevation,
          )
        : { resolved: [] as PathSurfaceOverlap[] };

    const { resolved: levelAboveOverlaps } =
      roomsAboveSurfaces.length > 0
        ? getPathSurfaceOverlap(
            roomsAboveSurfaceRTree,
            roomsAboveSurfaces,
            unresolvedOverlapPath,
            paramStart,
            level.levelAbove ? levels[level.levelAbove].floorElevation : undefined,
          )
        : { resolved: [] as PathSurfaceOverlap[] };

    const { leastDistSet: isleastDistSet, leastDist: leastDistToLevelOverlaps } = getLeastDistToPathSurfaceOverlap(
      levelOverlaps,
      nearestHeightInResolvedOverlap,
    );

    const { leastDistSet: isLeastDistAboveSet, leastDist: leastDistToLevelAboveOverlaps } =
      getLeastDistToPathSurfaceOverlap(levelAboveOverlaps, nearestHeightInResolvedOverlap);

    let overlapsFromCorrectLevel: PathSurfaceOverlap[];
    if (isleastDistSet && isLeastDistAboveSet) {
      overlapsFromCorrectLevel =
        leastDistToLevelOverlaps <= leastDistToLevelAboveOverlaps ? levelOverlaps : levelAboveOverlaps;
    } else if (isleastDistSet) {
      overlapsFromCorrectLevel = levelOverlaps;
    } else if (isLeastDistAboveSet) {
      overlapsFromCorrectLevel = levelAboveOverlaps;
    } else {
      for (const levelOverlap of levelOverlaps as UnresolvedPathSurfaceOverlap[]) {
        levelOverlap.height = undefined;
      }
      overlapsFromCorrectLevel = levelOverlaps;
    }

    allOverlaps.push(...overlapsFromCorrectLevel);
  }

  allOverlaps.sort((a, b) => a.paramStart - b.paramStart);

  const allOverlapsCount = allOverlaps.length;

  if (allOverlapsCount === 1) {
    return { overlaps: allOverlaps };
  }

  const mergedOverlaps: PathSurfaceOverlap[] = [];
  let currentOverlap: PathSurfaceOverlap = allOverlaps[0];
  for (let i = 1; i < allOverlapsCount; i++) {
    const { paramEnd, height } = allOverlaps[i];

    if (Math.abs(currentOverlap.height - height) < IGNORE_HEIGHT_CHANGE_THRESHOLD) {
      currentOverlap = { ...currentOverlap, paramEnd: paramEnd, rate: 0 };
    } else {
      mergedOverlaps.push({ ...currentOverlap });
      currentOverlap = allOverlaps[i];
    }

    if (i === allOverlapsCount - 1) {
      if (currentOverlap.paramEnd - currentOverlap.paramStart < IGNORE_GAP_THRESHOLD) {
        continue;
      }
      mergedOverlaps.push({ ...currentOverlap });
    }
  }
  return { overlaps: mergedOverlaps };
}

function getLeastDistToPathSurfaceOverlap(levelOverlaps: PathSurfaceOverlap[], nearestHeightInResolvedOverlap: number) {
  let leastDist = Number.MAX_VALUE;
  let leastDistSet = false;
  for (const overlap of levelOverlaps) {
    const { height: levelOverlapHeight } = overlap;
    const dist = Math.abs(nearestHeightInResolvedOverlap - levelOverlapHeight);

    if (dist < leastDist) {
      leastDist = dist;
      leastDistSet = true;
    }
  }
  return { leastDistSet, leastDist };
}

export interface GetMaxHeightOfSurfacesBelowPoint2Options {
  readonly level: Level;
  readonly levels: Table<Level>;
  readonly x: number;
  readonly y: number;
  readonly z: WorldInches; // z is used to make sure the surface is below the point
  readonly ignoreStairsAndRoomsAbove: boolean | undefined;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
}

/**
 * Traces a ray from the point (x, y, z) to the surfaces (room and stairs) below the point
 * and returns the maximum height of the surfaces below the point. Uses the RTree to efficiently
 * find the surfaces below the point.
 */
export function getMaxHeightOfSurfacesBelowPoint2({
  level,
  levels,
  x,
  y,
  z,
  ignoreStairsAndRoomsAbove = false,
  levelStairSurfaceRTree,
  levelStairSurfaces,
  levelPerimeterSurfaceRTree,
  levelPerimeterSurfaces,
}: GetMaxHeightOfSurfacesBelowPoint2Options): WorldInches {
  let maxHeight = worldInchesNew(Number.NEGATIVE_INFINITY);

  maxHeight = getMaxSurfaceHeightBelowPoint(
    levelPerimeterSurfaceRTree[level.id],
    x,
    y,
    z,
    levelPerimeterSurfaces[level.id],
    maxHeight,
    level.floorElevation ?? WORLD_ZERO_INCHES,
  );
  if (!ignoreStairsAndRoomsAbove && level.levelAbove) {
    maxHeight = getMaxSurfaceHeightBelowPoint(
      levelPerimeterSurfaceRTree[level.levelAbove],
      x,
      y,
      z,
      levelPerimeterSurfaces[level.levelAbove],
      maxHeight,
      levels[level.levelAbove].floorElevation ?? WORLD_ZERO_INCHES,
    );
  }
  if (!ignoreStairsAndRoomsAbove) {
    const stairSurfaceRTree = levelStairSurfaceRTree[level.id];
    const stairSurfaces = levelStairSurfaces[level.id];
    maxHeight = getMaxSurfaceHeightBelowPoint(stairSurfaceRTree, x, y, z, stairSurfaces, maxHeight);
  }
  // May return negative infinity!
  return maxHeight;
}

const NudgeUpwardsDistanceToBeAboveSurface = WORLD_ONE_INCH;

function getMaxSurfaceHeightBelowPoint(
  surfaceRTree: RBush<IndexAABB2>,
  x: number,
  y: number,
  z: WorldInches,
  surfaces: Surface[],
  maxHeight: WorldInches,
  additionalHeight: WorldInches = WORLD_ZERO_INCHES,
): WorldInches {
  const bbs = surfaceRTree.search({ minX: x, minY: y, maxX: x, maxY: y });
  for (const bb of bbs) {
    const surface = surfaces[bb.index];
    const height = levelInchesToWorld(surface.height, additionalHeight);
    if (height > maxHeight && height < numAdd(z, NudgeUpwardsDistanceToBeAboveSurface)) {
      if (classifyContainmentVec2PolyLine2Fast(x, y, surface.outline)) {
        maxHeight = height;
      }
    }
  }
  return maxHeight;
}
